import type { ReactNode } from "react";
import { useState } from "react";

import { BrandsView } from "@/components/brandHealth/BrandsView";
import { DecisionGridView } from "@/components/brandHealth/DecisionGridView";
import { EmailPendingTile } from "@/components/brandHealth/EmailPendingTile";
import { FallbackDepthPanel } from "@/components/brandHealth/FallbackDepthPanel";
import { LiveView } from "@/components/brandHealth/LiveView";
import { StaleDataBanner } from "@/components/brandHealth/StaleDataBanner";
import { ThresholdView } from "@/components/brandHealth/ThresholdView";
import { usePluginDriftPoll } from "@/hooks/usePluginDriftPoll";
import { useVigilBrandsPoll } from "@/hooks/useVigilBrandsPoll";
import { useVigilEmailStatus } from "@/hooks/useVigilEmailStatus";
import { useVigilSnapshotPoll } from "@/hooks/useVigilSnapshotPoll";
import type { PairWatchSnapshot } from "@/types/api";

/**
 * Brand Health — cross-brand observability (pair-watch decision grid, live
 * traffic, per-brand DNS/cert/mail health). Absorbed from the standalone
 * `products/x2mail-admin` SPA (AgiEu repo, Vigil dashboard) into this panel
 * per PK's single-panel consolidation directive (2026-07-15): "wszystko
 * administracyjne w jednym miejscu na admin.x2mail.eu".
 *
 * Four tabs, in-page state (not separate routes) — mirrors this panel's own
 * CampaignsPage precedent (list/wizard/progress as one route with internal
 * view state) rather than the source app's original multi-page layout.
 * FAIL-LOUD banner stays visible across every tab, exactly like the source
 * Dashboard.tsx: PK must see immediately when metrics stop flowing,
 * regardless of which tab he's looking at.
 */
type ActiveTab = "live" | "grid" | "threshold" | "brands";

export function BrandHealthPage() {
  const { snapshots, live, loading, error } = useVigilSnapshotPoll();
  const { brands, loading: brandsLoading, error: brandsError } = useVigilBrandsPoll();
  const { status: emailStatus, loading: emailLoading } = useVigilEmailStatus();
  const {
    events: driftEvents,
    loading: driftLoading,
    error: driftError,
    days: driftDays,
  } = usePluginDriftPoll();
  const [activeTab, setActiveTab] = useState<ActiveTab>("live");
  const [selectedSnapshot, setSelectedSnapshot] = useState<PairWatchSnapshot | null>(null);

  function handleCellClick(snapshot: PairWatchSnapshot) {
    setSelectedSnapshot(snapshot);
    setActiveTab("threshold");
  }

  function handleCloseThreshold() {
    setActiveTab("grid");
    setSelectedSnapshot(null);
  }

  // Determine the stale-data timestamp from the first available snapshot.
  const latestUpdatedAt = snapshots[0]?.updatedAt ?? null;

  return (
    <div className="rounded-lg bg-zinc-950 p-4 text-zinc-100 sm:p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-zinc-100">Brand Health</h2>
        <p className="text-xs text-zinc-500">
          Obserwowalność rodziny x2mail — ruch, decyzje pair-watch, zdrowie marek.
        </p>
      </div>

      {/* FAIL-LOUD: always on top when stale or missing */}
      <StaleDataBanner updatedAt={loading ? null : latestUpdatedAt} />

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-700 bg-red-950 px-4 py-3 text-sm text-red-400"
        >
          Błąd połączenia: {error}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap gap-1 border-b border-zinc-800">
        <TabButton active={activeTab === "live"} onClick={() => setActiveTab("live")}>
          Ruch na żywo
        </TabButton>
        <TabButton active={activeTab === "grid"} onClick={() => setActiveTab("grid")}>
          Siatka decyzyjna
        </TabButton>
        <TabButton active={activeTab === "brands"} onClick={() => setActiveTab("brands")}>
          Portfel marek
        </TabButton>
        {selectedSnapshot && (
          <TabButton active={activeTab === "threshold"} onClick={() => setActiveTab("threshold")}>
            Próg — {selectedSnapshot.pairId} {selectedSnapshot.country ?? "global"}{" "}
            {selectedSnapshot.brand ?? ""}
          </TabButton>
        )}
      </div>

      {/* Tab content */}
      {activeTab === "live" && (
        <div className="space-y-6">
          <LiveView live={live} loading={loading} />
          <EmailPendingTile status={emailStatus} loading={emailLoading} />
          <FallbackDepthPanel
            events={driftEvents}
            loading={driftLoading}
            error={driftError}
            days={driftDays}
          />
        </div>
      )}
      {activeTab === "grid" && (
        <DecisionGridView snapshots={snapshots} onCellClick={handleCellClick} />
      )}
      {activeTab === "brands" && (
        <BrandsView brands={brands} loading={brandsLoading} error={brandsError} />
      )}
      {activeTab === "threshold" && selectedSnapshot && (
        <ThresholdView snapshot={selectedSnapshot} onClose={handleCloseThreshold} />
      )}
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-4 py-2 text-sm transition-colors",
        active
          ? "border-b-2 border-indigo-500 text-indigo-300 -mb-px"
          : "text-zinc-500 hover:text-zinc-300",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
